// App.jsx: M3-8 thin router between RoomView (default) and GameView.
//
// Adding ?dev_game=1 to the URL mounts the M2 hot-seat GameView so M2
// development and the existing Playwright suite keep working through
// the M3-9..M3-12 build-out of the real room UI. The default route is
// the room, per the brief, the room is M3's entry point.
//
// Both views are independently rendered; switching is one-time at load
// (no in-app navigation yet, that lands with M3-12/M3-13). The Lua
// runtime is initialized lazily inside GameView's RealGame component,
// so RoomView's stub mount does not load wasmoon.
//
// M5 #3: kicks off the audio engine + iOS unlock listener. The engine
// is module-level so the first getAudioEngine() picks up the wired
// instance; here we only install the gesture listener.

import { useEffect, useState } from 'preact/hooks';
import { GameView } from './views/GameView.jsx';
import { RoomView } from './views/RoomView.jsx';
import { HowToPlayView } from './views/HowToPlayView.jsx';
import { OverlayNavContext } from './nav/HowToPlayTrigger.jsx';
import { subscribeHowToPlayRoomSettings, getHowToPlayRoomSettings } from './nav/how-to-play-settings.js';
import { useOverlayNav } from './nav/useOverlayNav.js';
import { getAudioEngine, installAudioUnlockListener } from './audio/index.js';
import { mountParticleLayer, getParticleLayer } from './ui/particle-layer-singleton.js';
import { applyCrtTier, effectiveTier, prefersReducedMotion } from './ui/crt-shader.js';
import { loadSettings, applyFontSetting } from './settings.js';
import { setCanvasSystemFont } from './canvas/text.js';
// M5 #8: CRT overlay stylesheet. Loaded once at app boot. CSS is inert
// until applyCrtTier() adds .crt-subtle / .crt-full to <html>.
import './styles/crt.css';

function isDevGameMode() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('dev_game') === '1';
}

export function App() {
  // #5b, overlay nav backbone. The overlay layer is mounted ABOVE the
  // live view tree below; opening How-to-Play never unmounts RoomView, so
  // the room WebSocket owner (useRoomWs) survives untouched.
  const overlay = useOverlayNav();
  // W6: the open How-to-Play panel follows the room's live target +
  // stick-the-dealer setting. RoomView publishes into the store; this
  // subscription re-renders the panel on every change while it is open.
  const [howToPlaySettings, setHowToPlaySettings] = useState(() => getHowToPlayRoomSettings());
  useEffect(() => subscribeHowToPlayRoomSettings(setHowToPlaySettings), []);

  useEffect(() => {
    // Touch the engine so the events bus binds at mount, then arm the
    // one-shot iOS unlock on first pointer/key.
    getAudioEngine();
    // M5 #5, mount the particle overlay so #10/#12/#15 consumers can
    // emit chip + fire bursts. Idempotent across re-mounts.
    mountParticleLayer();
    // M5 #8, apply the CRT tier from saved settings, gated by
    // prefers-reduced-motion. Tier changes via settings panel route
    // through useSettings, which calls applyCrtTier directly.
    const initialSettings = loadSettings();
    applyCrtTier(effectiveTier(initialSettings.crt, prefersReducedMotion()));
    // M5 #1, apply font setting from settings here too, since RoomView
    // (the default route) does not call useSettings. Without this, a
    // reload with system_font:true would never add the body class.
    applyFontSetting(initialSettings.system_font);
    setCanvasSystemFont(initialSettings.system_font);
    // Reduced-motion change listener, keep the particle layer + CRT
    // tier in sync.
    const mql = (typeof window !== 'undefined' && window.matchMedia)
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    const onMq = () => {
      getParticleLayer().setReducedMotion(!!mql?.matches);
      applyCrtTier(effectiveTier(loadSettings().crt, !!mql?.matches));
    };
    if (mql) mql.addEventListener('change', onMq);
    const detachAudio = installAudioUnlockListener();
    return () => {
      if (mql) mql.removeEventListener('change', onMq);
      detachAudio();
    };
  }, []);
  // The base view is a single top-level route:
  //   • ?dev_game=1 → GameView (the M2 hot-seat dev surface)
  //   • otherwise   → RoomView (the home/room lobby)
  // The overlay is a sibling rendered AFTER (on top of) the base view,
  // never a route that swaps the base out. The base view stays mounted the
  // whole time How-to-Play is open, so an in-room WS never disconnects. The
  // context exposes open() to the in-surface "?" triggers.
  return (
    <OverlayNavContext.Provider value={{ open: overlay.open }}>
      {isDevGameMode() ? <GameView /> : <RoomView />}
      {overlay.isOpen && <HowToPlayView onClose={overlay.close} {...howToPlaySettings} />}
    </OverlayNavContext.Provider>
  );
}
